/**
 * Starter Building Blocks (Insert ▸ Building Blocks): Page Parts, Calendars, Borders & Accents, Advertisements.
 *
 * Every block is a builder that receives the publication's colour palette (C), font pair (F), business information
 * and — for calendars — the chosen month, and returns { width, height, objects } in block-local coordinates. Blocks
 * are inserted as one editable group (Ungroup / double-click to edit the parts) and scaled down when they would not
 * fit the page.
 */
import { v4 as uuidv4 } from 'uuid';
import { SCHEME_PRESETS, tint } from './colors';
import { TEMPLATE_KIT, FONT_SCHEMES, BUSINESS_DEFAULTS, readableOn } from './templates';

const { palette, textBox, shape, rect, picture, heading, body, label, div, paras, bi, addressInline } = TEMPLATE_KIT;

export const BLOCK_CATEGORIES = ['Page Parts', 'Calendars', 'Borders & Accents', 'Advertisements'];
export const USER_CATEGORY = 'My Building Blocks';

/* ------------------------------------------------------------------ */
/* Context: palette + fonts from the current publication                */
/* ------------------------------------------------------------------ */

/** Most used heading / body fonts in the publication (falls back to the Modern font scheme). */
export const detectFonts = (pages = []) => {
  const bold = new Map();
  const normal = new Map();
  for (const page of pages) for (const o of page.objects || []) {
    if (o.type !== 'textbox' && o.type !== 'text') continue;
    const p = o.properties || {};
    if (!p.fontFamily) continue;
    const isHeading = p.fontWeight === 'bold' || Number(p.fontWeight) >= 600 || (p.fontSize || 0) >= 24;
    const m = isHeading ? bold : normal;
    m.set(p.fontFamily, (m.get(p.fontFamily) || 0) + 1);
  }
  const top = (m) => [...m.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  const headingFont = top(bold) || top(normal) || FONT_SCHEMES.modern.heading;
  const bodyFont = top(normal) || FONT_SCHEMES.modern.body;
  return { heading: headingFont, body: bodyFont };
};

export const blockContext = (state, calendar) => {
  const scheme = SCHEME_PRESETS[state.schemeId] || SCHEME_PRESETS.office;
  return {
    C: palette(scheme),
    F: detectFonts(state.pages),
    info: state.businessInfo || {},
    cal: calendar || defaultCalendar(),
  };
};

/* ------------------------------------------------------------------ */
/* Calendar helpers                                                     */
/* ------------------------------------------------------------------ */

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const defaultCalendar = (d = new Date()) => ({ year: d.getFullYear(), month: d.getMonth(), weekStart: 0 });

/** 6 rows × 7 columns of { day, inMonth, isToday } for the month (month is 0-based). */
export const monthMatrix = (year, month, weekStart = 0) => {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() - weekStart + 7) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const today = new Date();
  const rows = [];
  let n = 1 - lead;
  for (let r = 0; r < 6; r++) {
    const row = [];
    for (let c = 0; c < 7; c++, n++) {
      if (n < 1) row.push({ day: prevDays + n, inMonth: false });
      else if (n > daysInMonth) row.push({ day: n - daysInMonth, inMonth: false });
      else row.push({ day: n, inMonth: true, isToday: today.getFullYear() === year && today.getMonth() === month && today.getDate() === n });
    }
    rows.push(row);
  }
  return rows;
};

const weekdayNames = (weekStart, len = 3) => Array.from({ length: 7 }, (_, i) => DAY_NAMES[(i + weekStart) % 7].slice(0, len));

const calendarTable = (x, y, w, h, { C, F, cal }, opts = {}) => {
  const { borderWidth = 1, borderColor = tint(C.dark, 0.7), headerFill = C.primary, headerColor = C.onPrimary, dayColor = C.text, mutedColor = '#b4b4b4', todayFill = C.light, fontSize = 11, headerSize = 10, cellPadding = 4, showHeader = true, align = 'right', fill = { type: 'solid', color: '#ffffff' }, dayNameLength = 3 } = opts;
  const grid = monthMatrix(cal.year, cal.month, cal.weekStart);
  const cells = [];
  if (showHeader) cells.push(weekdayNames(cal.weekStart, dayNameLength).map((d) => ({ text: d, align: 'center', color: headerColor, bold: true, fontSize: headerSize, valign: 'middle' })));
  grid.forEach((row) => cells.push(row.map((cell) => ({ text: String(cell.day), align, color: cell.inMonth ? dayColor : mutedColor, fill: cell.isToday ? todayFill : undefined, bold: !!cell.isToday }))));
  return {
    id: uuidv4(), type: 'table', x, y, width: w, height: h, rotation: 0, locked: false, visible: true,
    properties: { rows: cells.length, cols: 7, cells, borderWidth, borderColor, fontFamily: F.body, fontSize, color: dayColor, headerRow: showHeader, headerFill, cellPadding, fill, opacity: 100, wrapStyle: 'none' },
  };
};

const monthTitle = (cal) => `${MONTH_NAMES[cal.month]} ${cal.year}`;

/* ------------------------------------------------------------------ */
/* Scaling                                                              */
/* ------------------------------------------------------------------ */

const scaleProps = (p, k) => {
  if (!p) return p;
  const out = { ...p };
  ['fontSize', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'cornerRadius', 'cellPadding', 'borderWidth', 'columnGap', 'wrapDistance'].forEach((key) => { if (typeof out[key] === 'number') out[key] = Math.max(0, out[key] * k); });
  if (out.stroke && typeof out.stroke.width === 'number') out.stroke = { ...out.stroke, width: out.stroke.width * k };
  if (Array.isArray(out.cells)) out.cells = out.cells.map((row) => row.map((c) => (c && typeof c.fontSize === 'number' ? { ...c, fontSize: c.fontSize * k } : c)));
  return out;
};

/** Uniformly scale a list of block-local objects (geometry + type sizes). */
export const scaleObjects = (objects, k) => (k === 1 ? objects : objects.map((o) => ({
  ...o, x: o.x * k, y: o.y * k, width: o.width * k, height: o.height * k,
  properties: scaleProps(o.properties, k),
  children: o.children ? scaleObjects(o.children, k) : undefined,
})));

/* ------------------------------------------------------------------ */
/* Blocks                                                               */
/* ------------------------------------------------------------------ */

const quoteText = '“The best way to predict the future is to create it.”';
const sidebarLines = ['Bring a reusable bag', 'Doors open at 9 AM', 'Free parking on Elm Street', 'Members save 10%'];

export const STARTER_BLOCKS = [
  /* ---------------- Page Parts ---------------- */
  {
    id: 'pp-heading-band', category: 'Page Parts', group: 'Headings', name: 'Heading — Colour Band',
    build: ({ C, F }) => ({ width: 520, height: 96, objects: [
      rect(0, 0, 520, 96, C.primary),
      rect(0, 88, 520, 8, C.strong),
      textBox(20, 12, 480, 24, div('Section'), label(F, C, 11, { color: readableOn(C.primary), letterSpacing: 4 })),
      textBox(20, 34, 480, 50, div('Your Heading Here'), heading(F, C, 30, { color: C.onPrimary, verticalAlign: 'middle', autofit: 'shrink' })),
    ] }),
  },
  {
    id: 'pp-heading-rule', category: 'Page Parts', group: 'Headings', name: 'Heading — Underlined',
    build: ({ C, F }) => ({ width: 520, height: 78, objects: [
      textBox(0, 0, 520, 52, div('Your Heading Here'), heading(F, C, 34, { color: C.dark, verticalAlign: 'bottom', autofit: 'shrink' })),
      rect(0, 60, 520, 1, tint(C.dark, 0.6)),
      rect(0, 58, 96, 5, C.secondary),
      textBox(0, 62, 520, 16, div('A short subheading or date goes here'), label(F, C, 9, { color: C.secondary, letterSpacing: 2, textTransform: 'none', fontWeight: 'normal', textAlign: 'right' })),
    ] }),
  },
  {
    id: 'pp-pullquote-bars', category: 'Page Parts', group: 'Pull Quotes', name: 'Pull Quote — Accent Bars',
    build: ({ C, F }) => ({ width: 300, height: 170, objects: [
      rect(0, 0, 300, 4, C.secondary),
      rect(0, 166, 300, 4, C.secondary),
      textBox(8, 14, 284, 112, div(quoteText), heading(F, C, 19, { color: C.dark, fontStyle: 'italic', fontWeight: 'normal', textAlign: 'center', verticalAlign: 'middle', lineHeight: 1.3 })),
      textBox(8, 128, 284, 30, div('— Peter Drucker'), label(F, C, 10, { color: C.primary, textAlign: 'center', letterSpacing: 2 })),
    ] }),
  },
  {
    id: 'pp-pullquote-mark', category: 'Page Parts', group: 'Pull Quotes', name: 'Pull Quote — Quotation Mark',
    build: ({ C, F }) => ({ width: 320, height: 180, objects: [
      textBox(0, 0, 120, 110, div('“'), heading(F, C, 120, { color: C.light, lineHeight: 0.9, paddingTop: 0, paddingLeft: 0 })),
      rect(0, 40, 6, 130, C.primary),
      textBox(24, 40, 290, 100, div(quoteText), body(F, C, 17, { color: C.dark, fontStyle: 'italic', verticalAlign: 'middle', lineHeight: 1.35 })),
      textBox(24, 140, 290, 30, div('<b>Peter Drucker</b>, Management consultant'), body(F, C, 11, { color: C.secondary })),
    ] }),
  },
  {
    id: 'pp-sidebar-tint', category: 'Page Parts', group: 'Sidebars', name: 'Sidebar — Tinted',
    build: ({ C, F }) => ({ width: 240, height: 260, objects: [
      rect(0, 0, 240, 260, C.light, { cornerRadius: 8 }),
      rect(0, 0, 240, 44, C.primary, { cornerRadius: 8 }),
      rect(0, 30, 240, 14, C.primary),
      textBox(16, 6, 208, 34, div('Good to Know'), heading(F, C, 16, { color: C.onPrimary, verticalAlign: 'middle' })),
      textBox(16, 56, 208, 190, paras(...sidebarLines.map((l) => `• ${l}`)), body(F, C, 12.5, { color: C.dark, lineHeight: 1.8 })),
    ] }),
  },
  {
    id: 'pp-sidebar-dark', category: 'Page Parts', group: 'Sidebars', name: 'Sidebar — Bold',
    build: ({ C, F }) => ({ width: 240, height: 260, objects: [
      rect(0, 0, 240, 260, C.dark),
      rect(0, 0, 8, 260, C.strong),
      textBox(24, 18, 200, 28, div('Did you know?'), label(F, C, 10, { color: C.strong, letterSpacing: 3 })),
      textBox(24, 44, 200, 70, div('72%'), heading(F, C, 52, { color: C.onDark, lineHeight: 1 })),
      textBox(24, 118, 200, 124, div('of our members joined through a friend. Refer someone this month and you both receive a free workshop pass.'), body(F, C, 12.5, { color: C.onDark, lineHeight: 1.55 })),
    ] }),
  },
  {
    id: 'pp-story', category: 'Page Parts', group: 'Stories', name: 'Story — Two Columns',
    build: ({ C, F }) => ({ width: 520, height: 300, objects: [
      textBox(0, 0, 520, 44, div('Story Headline Goes Here'), heading(F, C, 26, { color: C.dark, verticalAlign: 'bottom' })),
      textBox(0, 46, 520, 20, div('By Author Name · Staff Writer'), label(F, C, 9, { color: C.secondary, textTransform: 'none', fontWeight: 'normal', letterSpacing: 1 })),
      rect(0, 70, 520, 1, C.secondary),
      textBox(0, 78, 520, 222, paras('This is the opening paragraph of your story. Keep it short and give readers a reason to keep going — who, what, where and why in two or three sentences.', 'The second paragraph adds detail and colour. Replace this text with your own, or paste a longer article and let it flow across both columns automatically.', 'Finish with a clear next step: where to learn more, who to contact, or what happens next.'), body(F, C, 11.5, { columns: 2, columnGap: 18, lineHeight: 1.5 })),
    ] }),
  },
  {
    id: 'pp-toc', category: 'Page Parts', group: 'Tables of Contents', name: 'Table of Contents',
    build: ({ C, F }) => {
      const rows = [['Letter from the Director', '2'], ['Community Spotlight', '3'], ['Upcoming Events', '4'], ['Volunteer Corner', '6'], ['Contact & Hours', '8']];
      return { width: 240, height: 250, objects: [
        rect(0, 0, 240, 250, '#ffffff', { stroke: { color: C.primary, width: 1.5 } }),
        rect(0, 0, 240, 38, C.primary),
        textBox(14, 4, 212, 30, div('Inside this issue'), heading(F, C, 15, { color: C.onPrimary, verticalAlign: 'middle' })),
        { id: uuidv4(), type: 'table', x: 12, y: 48, width: 216, height: 190, rotation: 0, locked: false, visible: true, properties: { rows: rows.length, cols: 2, cells: rows.map(([t, p]) => [{ text: t, valign: 'middle' }, { text: p, align: 'right', bold: true, color: C.primary, valign: 'middle' }]), borderWidth: 0, borderColor: 'transparent', fontFamily: F.body, fontSize: 12, color: C.text, headerRow: false, banded: true, cellPadding: 6, fill: { type: 'none' }, opacity: 100, wrapStyle: 'none' } },
      ] };
    },
  },
  {
    id: 'pp-photo-caption', category: 'Page Parts', group: 'Pictures', name: 'Picture with Caption',
    build: ({ C, F }) => ({ width: 300, height: 250, objects: [
      picture(0, 0, 300, 200, 'city skyline sunset', { stroke: { color: C.light, width: 6 } }),
      rect(0, 206, 40, 3, C.strong),
      textBox(0, 212, 300, 38, div('<b>Caption:</b> describe the picture in one or two lines. Photo credit goes here.'), body(F, C, 10.5, { color: C.dark, fontStyle: 'italic', lineHeight: 1.4 })),
    ] }),
  },

  /* ---------------- Calendars ---------------- */
  {
    id: 'cal-classic', category: 'Calendars', group: 'Month', name: 'Classic Grid', calendar: true,
    build: (ctx) => ({ width: 420, height: 320, objects: [
      textBox(0, 0, 420, 40, div(monthTitle(ctx.cal)), heading(ctx.F, ctx.C, 24, { color: ctx.C.dark, verticalAlign: 'middle' })),
      rect(0, 42, 420, 2, ctx.C.primary),
      calendarTable(0, 50, 420, 270, ctx),
    ] }),
  },
  {
    id: 'cal-banner', category: 'Calendars', group: 'Month', name: 'Banner Month', calendar: true,
    build: (ctx) => ({ width: 420, height: 340, objects: [
      rect(0, 0, 420, 62, ctx.C.primary),
      textBox(16, 6, 300, 50, div(MONTH_NAMES[ctx.cal.month]), heading(ctx.F, ctx.C, 30, { color: ctx.C.onPrimary, verticalAlign: 'middle' })),
      textBox(300, 6, 104, 50, div(String(ctx.cal.year)), heading(ctx.F, ctx.C, 22, { color: readableOn(ctx.C.primary), textAlign: 'right', verticalAlign: 'middle', fontWeight: 'normal' })),
      calendarTable(0, 62, 420, 278, ctx, { headerFill: ctx.C.dark, headerColor: ctx.C.onDark, borderColor: tint(ctx.C.dark, 0.8) }),
    ] }),
  },
  {
    id: 'cal-compact', category: 'Calendars', group: 'Month', name: 'Compact', calendar: true,
    build: (ctx) => ({ width: 220, height: 190, objects: [
      textBox(0, 0, 220, 24, div(monthTitle(ctx.cal)), heading(ctx.F, ctx.C, 12.5, { color: ctx.C.primary, textAlign: 'center', verticalAlign: 'middle' })),
      calendarTable(0, 26, 220, 164, ctx, { fontSize: 8.5, headerSize: 7.5, cellPadding: 2, borderWidth: 0.5, headerFill: ctx.C.light, headerColor: ctx.C.dark, align: 'center', dayNameLength: 1 }),
    ] }),
  },
  {
    id: 'cal-notes', category: 'Calendars', group: 'Month', name: 'Month with Notes', calendar: true,
    build: (ctx) => ({ width: 560, height: 320, objects: [
      textBox(0, 0, 400, 40, div(monthTitle(ctx.cal)), heading(ctx.F, ctx.C, 24, { color: ctx.C.dark, verticalAlign: 'middle' })),
      calendarTable(0, 48, 400, 272, ctx),
      rect(416, 0, 144, 320, ctx.C.light, { cornerRadius: 6 }),
      textBox(428, 8, 120, 28, div('Notes'), label(ctx.F, ctx.C, 10, { color: ctx.C.primary, letterSpacing: 3 })),
      ...Array.from({ length: 9 }, (_, i) => rect(428, 60 + i * 28, 120, 1, tint(ctx.C.dark, 0.72))),
    ] }),
  },
  {
    id: 'cal-minimal', category: 'Calendars', group: 'Month', name: 'Minimal', calendar: true,
    build: (ctx) => ({ width: 380, height: 300, objects: [
      textBox(0, 0, 380, 44, div(MONTH_NAMES[ctx.cal.month].toUpperCase()) , heading(ctx.F, ctx.C, 26, { color: ctx.C.dark, letterSpacing: 6, fontWeight: 'normal', verticalAlign: 'middle' })),
      textBox(0, 44, 380, 18, div(String(ctx.cal.year)), label(ctx.F, ctx.C, 9, { color: ctx.C.secondary, letterSpacing: 4, textAlign: 'left' })),
      calendarTable(0, 70, 380, 230, ctx, { borderWidth: 0, borderColor: 'transparent', headerFill: 'transparent', headerColor: ctx.C.secondary, align: 'center', fill: { type: 'none' }, fontSize: 11.5, todayFill: ctx.C.light }),
    ] }),
  },
  {
    id: 'cal-photo', category: 'Calendars', group: 'Month', name: 'Photo Calendar', calendar: true,
    build: (ctx) => ({ width: 420, height: 470, objects: [
      picture(0, 0, 420, 170, 'nature landscape seasonal'),
      rect(0, 170, 420, 44, ctx.C.primary),
      textBox(16, 170, 388, 44, div(monthTitle(ctx.cal)), heading(ctx.F, ctx.C, 20, { color: ctx.C.onPrimary, verticalAlign: 'middle' })),
      calendarTable(0, 222, 420, 248, ctx, { headerFill: ctx.C.light, headerColor: ctx.C.dark }),
    ] }),
  },

  /* ---------------- Borders & Accents ---------------- */
  {
    id: 'ba-frame-thin', category: 'Borders & Accents', group: 'Frames', name: 'Frame — Thin',
    build: ({ C }) => ({ width: 320, height: 240, objects: [shape('frame-simple', 0, 0, 320, 240, { fill: { type: 'solid', color: C.primary } })] }),
  },
  {
    id: 'ba-frame-double', category: 'Borders & Accents', group: 'Frames', name: 'Frame — Double Line',
    build: ({ C }) => ({ width: 320, height: 240, objects: [shape('frame-double', 0, 0, 320, 240, { fill: { type: 'solid', color: C.dark } })] }),
  },
  {
    id: 'ba-corners', category: 'Borders & Accents', group: 'Frames', name: 'Corner Brackets',
    build: ({ C }) => {
      const L = 36, t = 4, W = 320, H = 240, col = C.secondary;
      return { width: W, height: H, objects: [
        rect(0, 0, L, t, col), rect(0, 0, t, L, col),
        rect(W - L, 0, L, t, col), rect(W - t, 0, t, L, col),
        rect(0, H - t, L, t, col), rect(0, H - L, t, L, col),
        rect(W - L, H - t, L, t, col), rect(W - t, H - L, t, L, col),
      ] };
    },
  },
  {
    id: 'ba-rule-diamond', category: 'Borders & Accents', group: 'Dividers', name: 'Divider — Diamond',
    build: ({ C }) => ({ width: 400, height: 16, objects: [
      rect(0, 7, 186, 1.5, C.dark), rect(214, 7, 186, 1.5, C.dark),
      shape('diamond', 194, 2, 12, 12, { fill: { type: 'solid', color: C.strong } }),
    ] }),
  },
  {
    id: 'ba-rule-dotted', category: 'Borders & Accents', group: 'Dividers', name: 'Divider — Dotted',
    build: ({ C }) => ({ width: 400, height: 8, objects: [shape('line', 0, 0, 400, 8, { fill: { type: 'none' }, stroke: { color: C.secondary, width: 3, dash: 'dotted' } })] }),
  },
  {
    id: 'ba-ribbon', category: 'Borders & Accents', group: 'Ribbons & Badges', name: 'Ribbon Banner',
    build: ({ C, F }) => ({ width: 300, height: 84, objects: [
      shape('ribbon-up', 0, 0, 300, 84, { fill: { type: 'solid', color: C.strong } }),
      textBox(40, 22, 220, 40, div('Special Announcement'), heading(F, C, 15, { color: C.onStrong, textAlign: 'center', verticalAlign: 'middle', textTransform: 'uppercase', letterSpacing: 1 })),
    ] }),
  },
  {
    id: 'ba-badge-seal', category: 'Borders & Accents', group: 'Ribbons & Badges', name: 'Seal Badge',
    build: ({ C, F }) => ({ width: 120, height: 120, objects: [
      shape('badge-seal', 0, 0, 120, 120, { fill: { type: 'solid', color: C.secondary } }),
      textBox(16, 30, 88, 60, div('new', 'font-size: 11px; letter-spacing: 3px') + div('2025'), heading(F, C, 22, { color: C.onSecondary, textAlign: 'center', verticalAlign: 'middle', lineHeight: 1.05 })),
    ] }),
  },
  {
    id: 'ba-edge-bands', category: 'Borders & Accents', group: 'Page Edges', name: 'Page Edge Bands',
    build: ({ C }) => ({ width: 612, height: 48, objects: [rect(0, 0, 612, 36, C.primary), rect(0, 40, 612, 8, C.secondary)] }),
  },
  {
    id: 'ba-scroll', category: 'Borders & Accents', group: 'Ribbons & Badges', name: 'Scroll Label',
    build: ({ C, F }) => ({ width: 260, height: 70, objects: [
      shape('horizontal-scroll', 0, 0, 260, 70, { fill: { type: 'solid', color: C.pale }, stroke: { color: C.dark, width: 1 } }),
      textBox(30, 16, 200, 38, div('Est. 1998'), heading(F, C, 18, { color: C.dark, textAlign: 'center', verticalAlign: 'middle', fontStyle: 'italic', fontWeight: 'normal' })),
    ] }),
  },

  /* ---------------- Advertisements ---------------- */
  {
    id: 'ad-coupon', category: 'Advertisements', group: 'Coupons', name: 'Coupon — Dashed',
    build: ({ C, F, info }) => ({ width: 360, height: 160, objects: [
      rect(0, 0, 360, 160, '#ffffff', { stroke: { color: C.dark, width: 1.5, dash: 'dashed' }, cornerRadius: 8 }),
      rect(0, 0, 118, 160, C.primary, { cornerRadius: 8 }),
      rect(100, 0, 18, 160, C.primary),
      textBox(6, 30, 106, 60, div('20%'), heading(F, C, 40, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle', lineHeight: 1 })),
      textBox(6, 88, 106, 30, div('off'), label(F, C, 14, { color: readableOn(C.primary), textAlign: 'center', letterSpacing: 4 })),
      textBox(130, 14, 220, 30, div('Your next purchase'), heading(F, C, 17, { color: C.dark })),
      textBox(130, 46, 220, 62, div(`Present this coupon at ${bi(info, 'organization')}. One per customer. Not valid with other offers.`), body(F, C, 10, { color: C.text, lineHeight: 1.45 })),
      textBox(130, 112, 150, 34, div('CODE: SAVE20'), label(F, C, 10, { color: C.dark, letterSpacing: 2, verticalAlign: 'middle', background: { type: 'solid', color: C.light }, paddingLeft: 10 })),
      textBox(290, 108, 60, 40, div('Exp. 12/31'), body(F, C, 9, { color: C.secondary, textAlign: 'right', verticalAlign: 'middle' })),
      textBox(326, 0, 30, 30, div('✂'), body(F, C, 16, { color: C.dark, textAlign: 'center', paddingTop: 0 })),
    ] }),
  },
  {
    id: 'ad-sale-burst', category: 'Advertisements', group: 'Sales', name: 'Sale Burst',
    build: ({ C, F }) => ({ width: 200, height: 200, objects: [
      shape('star-12', 0, 0, 200, 200, { fill: { type: 'solid', color: C.strong }, stroke: { color: '#ffffff', width: 3 } }),
      textBox(30, 44, 140, 110, div('SALE', 'letter-spacing: 3px') + div('up to', 'font-size: 12px; letter-spacing: 3px; font-weight: normal') + div('50% off', 'font-size: 22px'), heading(F, C, 30, { color: C.onStrong, textAlign: 'center', verticalAlign: 'middle', lineHeight: 1.1 })),
    ] }),
  },
  {
    id: 'ad-event', category: 'Advertisements', group: 'Events', name: 'Event Announcement',
    build: ({ C, F, info }) => ({ width: 360, height: 220, objects: [
      rect(0, 0, 360, 220, C.pale),
      rect(0, 0, 360, 70, C.dark),
      textBox(18, 8, 324, 24, div('Save the date'), label(F, C, 10, { color: C.strong, letterSpacing: 3 })),
      textBox(18, 30, 324, 36, div('Annual Open House'), heading(F, C, 22, { color: C.onDark, verticalAlign: 'middle' })),
      textBox(18, 82, 160, 70, div('<b>Saturday, June 21</b>') + div('6:00 – 9:00 PM') + div('Free admission'), body(F, C, 12.5, { color: C.dark, lineHeight: 1.6 })),
      rect(186, 84, 1, 66, C.secondary),
      textBox(198, 82, 150, 70, div(`<b>${bi(info, 'organization')}</b>`) + div(addressInline(info)), body(F, C, 11, { color: C.dark, lineHeight: 1.5 })),
      textBox(18, 166, 324, 38, div(`RSVP · ${bi(info, 'phone')} · ${bi(info, 'website')}`), label(F, C, 10, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle', letterSpacing: 1, background: { type: 'solid', color: C.primary }, cornerRadius: 19 })),
    ] }),
  },
  {
    id: 'ad-grand-opening', category: 'Advertisements', group: 'Events', name: 'Grand Opening',
    build: ({ C, F, info }) => ({ width: 300, height: 300, objects: [
      rect(0, 0, 300, 300, C.primary),
      shape('frame-simple', 12, 12, 276, 276, { fill: { type: 'solid', color: readableOn(C.primary) }, opacity: 60 }),
      textBox(30, 40, 240, 30, div('You are invited to our'), label(F, C, 10, { color: readableOn(C.primary), textAlign: 'center', letterSpacing: 3, fontWeight: 'normal' })),
      textBox(30, 72, 240, 110, div('Grand') + div('Opening'), heading(F, C, 44, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle', lineHeight: 1, textTransform: 'uppercase', autofit: 'shrink' })),
      textBox(30, 190, 240, 44, div('<b>Saturday, March 1 · 10 AM</b>') + div(addressInline(info)), body(F, C, 11.5, { color: readableOn(C.primary), textAlign: 'center', lineHeight: 1.5 })),
      textBox(70, 244, 160, 30, div('First 50 guests get a gift'), label(F, C, 9, { color: C.onStrong, textAlign: 'center', verticalAlign: 'middle', background: { type: 'solid', color: C.strong }, cornerRadius: 15, letterSpacing: 1 })),
    ] }),
  },
  {
    id: 'ad-product-card', category: 'Advertisements', group: 'Products', name: 'Product Card',
    build: ({ C, F }) => ({ width: 220, height: 300, objects: [
      rect(0, 0, 220, 300, '#ffffff', { stroke: { color: tint(C.dark, 0.75), width: 1 }, cornerRadius: 10 }),
      picture(12, 12, 196, 160, 'product photo studio', { borderRadius: 6 }),
      shape('badge-circle', 156, 0, 64, 64, { fill: { type: 'solid', color: C.strong } }),
      textBox(156, 12, 64, 40, div('$49'), heading(F, C, 18, { color: C.onStrong, textAlign: 'center', verticalAlign: 'middle' })),
      textBox(14, 180, 192, 30, div('Product Name'), heading(F, C, 16, { color: C.dark, verticalAlign: 'middle' })),
      textBox(14, 208, 192, 54, div('A one-line benefit, a key feature and the colours or sizes it comes in.'), body(F, C, 10.5, { color: C.text, lineHeight: 1.45 })),
      textBox(14, 262, 192, 28, div('Shop now →'), label(F, C, 10, { color: C.onPrimary, textAlign: 'center', verticalAlign: 'middle', background: { type: 'solid', color: C.primary }, cornerRadius: 6, letterSpacing: 1 })),
    ] }),
  },
  {
    id: 'ad-contact-strip', category: 'Advertisements', group: 'Contact', name: 'Contact Strip',
    build: ({ C, F, info }) => ({ width: 520, height: 64, objects: [
      rect(0, 0, 520, 64, C.light, { cornerRadius: 6 }),
      rect(0, 0, 8, 64, C.primary, { cornerRadius: 6 }),
      textBox(20, 8, 200, 48, div(`<b>${bi(info, 'organization')}</b>`) + div(bi(info, 'tagline'), 'font-size: 10px'), body(F, C, 13, { color: C.dark, verticalAlign: 'middle', lineHeight: 1.35 })),
      textBox(230, 8, 150, 48, div(`☏ ${bi(info, 'phone')}`) + div(`✉ ${bi(info, 'email')}`), body(F, C, 10.5, { color: C.text, verticalAlign: 'middle', lineHeight: 1.6 })),
      textBox(380, 8, 130, 48, div(bi(info, 'website')) + div(addressInline(info), 'font-size: 9px'), body(F, C, 10.5, { color: C.primary, textAlign: 'right', verticalAlign: 'middle', lineHeight: 1.6 })),
    ] }),
  },
  {
    id: 'ad-testimonial', category: 'Advertisements', group: 'Testimonials', name: 'Testimonial',
    build: ({ C, F }) => ({ width: 300, height: 170, objects: [
      rect(0, 0, 300, 170, '#ffffff', { stroke: { color: C.secondary, width: 1 }, cornerRadius: 10 }),
      textBox(16, 10, 268, 24, div('★★★★★'), body(F, C, 16, { color: C.strong, letterSpacing: 2 })),
      textBox(16, 36, 268, 84, div('“Friendly staff, quick service and the best coffee on Main Street. We come back every week.”'), body(F, C, 13, { color: C.dark, fontStyle: 'italic', lineHeight: 1.45 })),
      shape('circle', 16, 124, 32, 32, { fill: { type: 'solid', color: C.light } }),
      textBox(56, 122, 228, 36, div('<b>Jordan Lee</b>') + div('Regular since 2019', 'font-size: 10px'), body(F, C, 11.5, { color: C.text, verticalAlign: 'middle', lineHeight: 1.35 })),
    ] }),
  },
  {
    id: 'ad-hiring', category: 'Advertisements', group: 'Notices', name: 'We\u2019re Hiring',
    build: ({ C, F, info }) => ({ width: 300, height: 260, objects: [
      rect(0, 0, 300, 260, C.dark, { cornerRadius: 8 }),
      rect(0, 0, 300, 8, C.strong, { cornerRadius: 8 }),
      textBox(20, 22, 260, 44, div('We\u2019re Hiring!'), heading(F, C, 30, { color: C.onDark, verticalAlign: 'middle' })),
      textBox(20, 66, 260, 22, div('Join a team that loves what it does'), body(F, C, 11.5, { color: tint(C.dark, 0.7), fontStyle: 'italic' })),
      textBox(20, 96, 260, 96, paras('• Front-of-house associate (part time)', '• Weekend barista', '• Delivery driver — flexible hours', '• Paid training & staff discount'), body(F, C, 11.5, { color: C.onDark, lineHeight: 1.75 })),
      textBox(20, 206, 260, 34, div(`Apply at ${bi(info, 'email')}`), label(F, C, 10, { color: C.onStrong, textAlign: 'center', verticalAlign: 'middle', background: { type: 'solid', color: C.strong }, cornerRadius: 17, letterSpacing: 1, textTransform: 'none' })),
    ] }),
  },
];

/* ------------------------------------------------------------------ */
/* Build / insert                                                       */
/* ------------------------------------------------------------------ */

export const blocksFor = (category) => STARTER_BLOCKS.filter((b) => b.category === category);
export const blockById = (id) => STARTER_BLOCKS.find((b) => b.id === id);

const relayer = (objects) => objects.map((o, i) => ({ ...o, layer: i }));

/** Build a starter block as a single group object (block-local origin), optionally scaled by k. */
export const buildBlockObject = (block, ctx, k = 1) => {
  const spec = block.build(ctx);
  const objects = relayer(scaleObjects(spec.objects, k));
  const bounds = objects.reduce((b, o) => ({ w: Math.max(b.w, o.x + o.width), h: Math.max(b.h, o.y + o.height) }), { w: 0, h: 0 });
  return {
    type: 'group', x: 0, y: 0, width: spec.width * k, height: spec.height * k, rotation: 0, locked: false, visible: true,
    properties: { name: block.name, buildingBlockId: block.id },
    children: objects,
    __bounds: bounds,
  };
};

const placeOnPage = (obj, page) => {
  // Centre, then cascade a little when something already sits at exactly that spot (repeated inserts stay visible).
  let x = Math.round((page.width - obj.width) / 2);
  let y = Math.round((page.height - obj.height) / 2);
  const taken = (px, py) => (page.objects || []).some((o) => Math.abs(o.x - px) < 2 && Math.abs(o.y - py) < 2);
  let guard = 0;
  while (taken(x, y) && guard++ < 12 && x + obj.width + 24 <= page.width && y + obj.height + 24 <= page.height) { x += 24; y += 24; }
  return { ...obj, x, y };
};

/**
 * Prepare a block for insertion on a page: scale to fit inside the page margins, centre it, and give every object a
 * fresh id so the same block can be inserted many times.
 */
export const blockForPage = (block, ctx, page, margin = 36) => {
  const spec = block.build(ctx);
  const maxW = Math.max(40, page.width - margin * 2);
  const maxH = Math.max(40, page.height - margin * 2);
  const k = Math.min(1, maxW / spec.width, maxH / spec.height);
  const group = buildBlockObject(block, ctx, k);
  delete group.__bounds;
  group.children = group.children.map((c) => ({ ...c, id: uuidv4() }));
  return placeOnPage(group, page);
};

/** A saved (user) block object, scaled to fit the page and centred. */
export const userBlockForPage = (object, page, margin = 36) => {
  const { id, layer, ...rest } = object;
  const maxW = Math.max(40, page.width - margin * 2);
  const maxH = Math.max(40, page.height - margin * 2);
  const k = Math.min(1, maxW / (rest.width || 1), maxH / (rest.height || 1));
  const [scaled] = scaleObjects([{ ...rest, x: 0, y: 0 }], k);
  if (scaled.children) scaled.children = scaled.children.map((c) => ({ ...c, id: uuidv4() }));
  return placeOnPage(scaled, page);
};

export const BUSINESS_PLACEHOLDERS = BUSINESS_DEFAULTS;
